'use client'

import React, { useEffect, useState, useCallback } from "react";
import { useFormik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@/redux/store';
import { useGetFTLRowDetailsQuery, useCreateFTLRowsMutation } from '@/redux/slices/preship/preship-api-slice';
import { useGetPalletsQuery, useUpdatePalletMutation } from "@/redux/slices/pallet/palletApiSlice";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { ChevronsLeft, ChevronsRight, Loader2 } from "lucide-react";
import { showSuccessToast, showErrorToast } from "../toast/toast";
import { Button } from "../ui/button";
import { useUpdatePalletFtlFieldsMutation } from "@/redux/slices/define/defineApiSlice";
import { setProjectFtlFields } from "@/redux/slices/define/defineSlice";
import FTLConsolidatedView from "./FtlConsolidatedView";
import EditBanner from './EditBanner';
import { useGetFTLConsolidatedDataQuery } from "@/redux/slices/pallet/palletApiSlice";
import { FtlService } from "@/services/FtlService";
import { refetchAfterSave } from "@/lib/refetchAfterSave";
interface FTLRow {
  id?: number;
  variant: string;
  topMeasured: string;
  middleMeasured: string;
  bottomMeasured: string;
  notes: string;
  palletLpnNumberId?: number;
  ftlState: string;
}

interface PalletLpnNumber {
  id?: number;
  lpnNo: string;
  endStatusPallet: string;

  ftlRows: FTLRow[];
}

interface FormValues {
  palletLpnNumbers: PalletLpnNumber[];
}

interface FtlProps {
  variant: 'preShip' | 'postShip' | 'postStack';
  week: string[];
  palletId?: number;
  isEditing?: boolean;
  onFormChange?: (hasChanges: boolean) => void;
  isOffline?: boolean;
  onOfflineSubmit?: (values: any) => Promise<void>;
  setIsLoading: (isLoading: boolean) => void;
}

const Ftl: React.FC<FtlProps> = ({ variant, week, palletId, onFormChange, isOffline, tabName, onOfflineSubmit, setIsLoading, stage }) => {
  const { activePallet } = useSelector((state: RootState) => state.pallets);
  const [showPalletNo, setShowPalletNo] = useState(true);
  const [showLpnNo, setShowLpnNo] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [showEndStatus, setShowEndStatus] = useState(true);
  const [isConsolidatedViewOpen, setIsConsolidatedViewOpen] = useState(false);
  const [isDataReady, setIsDataReady] = useState(false);
  const [initialValues, setInitialValues] = useState<FormValues | null>(null);
  const { projectId } = useSelector((state: RootState) => state.define);
  const dispatch = useDispatch();
  const [offlineFtlData, setOfflineFtlData] = useState<any>(null);
  const { data: consolidatedData, isLoading: isConsolidatedDataLoading, error: consolidatedDataError, refetch: refetchConsolidatedData } = useGetFTLConsolidatedDataQuery({ projectId, variant }, { refetchOnMountOrArgChange: true });

  const [isAdvancedControlsOpen, setIsAdvancedControlsOpen] = useState(false);
  const { data: pallets, refetch: refetchPallets, isFetching } = useGetPalletsQuery(projectId, {});
  const { data: ftlData, isLoading: isFtlDataLoading, error: ftlDataError, refetch: refetchFtlData } = useGetFTLRowDetailsQuery({ palletId: activePallet?.id, variant }, {
    skip: !activePallet,
    refetchOnMountOrArgChange: true
  });
  console.log(ftlData, 'ftlData')
  const [createFTLRows] = useCreateFTLRowsMutation();
  const [updateProjectFtlFields, { isLoading: updateProjectLoading }] = useUpdatePalletFtlFieldsMutation();

  const paramsFormik = useFormik<any>({
    initialValues: {
      plusByUser: activePallet?.plusByUser,
      percentage: activePallet?.percentage,
    },
    onSubmit: async (values) => {
      try {
        const res = await updateProjectFtlFields({ ...values, palletId: activePallet?.id, projectId }).unwrap();
        showSuccessToast("Successfully updated!");
        refetchAfterSave(refetchPallets);
        refetchAfterSave(refetchConsolidatedData);
      } catch (e) {
        console.log(e);
        showErrorToast("Failed to update.");
      }
    }
  });

  const formik = useFormik<FormValues>({
    initialValues: {
      palletLpnNumbers: [],
    },
    onSubmit: async (values) => {
      try {
        if (isOffline) {
          try {
            await FtlService.createFTLRows({
              palletId: Number(palletId),
              palletLpnNumbers: values.palletLpnNumbers
            });
            showSuccessToast("Successfully saved FTL details!");
          } catch (error) {
            console.error('Error saving FTL details:', error);
            showErrorToast("Failed to save FTL details");
          }
          return;
        }
        const payload = {
          palletId: activePallet?.id,
          palletLpnNumbers: values.palletLpnNumbers.map(pln => ({
            ...pln,
            ftlRows: pln.ftlRows.map(row => ({
              ...row,
              variant,
            })),
          })),
        };
        console.log(payload, 'creaate ftl row')
        await createFTLRows(payload).unwrap();
        showSuccessToast("Successfully saved!");
      } catch (error) {
        showErrorToast("Failed to save FTL rows");
        console.error('Failed to save FTL rows:', error);
      }
    },
  });
  useEffect(() => {
    if (isOffline) {
      const getData = async () => {
        try {
          const res = await FtlService.getFTLDetails(Number(palletId), variant);
          setOfflineFtlData(res);
        } catch (e) {
          console.error('Error getting FTL data:', e);
        }
      };
      getData();
    }
  }, [isOffline, activePallet]);
  useEffect(() => {
    if (activePallet && !isFtlDataLoading && !isOffline) {
      let initialPalletLpnNumbers: PalletLpnNumber[] = [];

      if (ftlDataError && 'status' in ftlDataError && ftlDataError.status === 404) {
        initialPalletLpnNumbers = Array.from({ length: Number(activePallet.numberOfPallets) }, (_, index): PalletLpnNumber => ({
          lpnNo: '',
          endStatusPallet: '',
          // ftlState: 'yes',
          ftlRows: [{
            variant,
            topMeasured: '',
            middleMeasured: '',
            bottomMeasured: '',
            notes: '',
            ftlState: 'yes',
          }],
        }));
      } else if (ftlData?.palletLpnNumbers && ftlData.palletLpnNumbers.length > 0) {
        initialPalletLpnNumbers = ftlData.palletLpnNumbers.map((pln: PalletLpnNumber) => ({
          ...pln,
          endStatusPallet: pln.endStatusPallet || '',

          ftlRows: pln.ftlRows.length > 0 ? pln.ftlRows.map(row => ({ ...row })) : [{
            variant,
            topMeasured: '',
            middleMeasured: '',
            bottomMeasured: '',
            notes: '',
            ftlState: 'yes',
          }],
        }));
      }

      const mutablePalletLpnNumbers = [...initialPalletLpnNumbers];

      while (mutablePalletLpnNumbers.length < Number(activePallet.numberOfPallets)) {
        mutablePalletLpnNumbers.push({
          lpnNo: '',
          endStatusPallet: '',
          ftlRows: [{
            variant,
            topMeasured: '',
            middleMeasured: '',
            bottomMeasured: '',
            ftlState: 'yes',
            notes: '',
          }],
        });
      }

      const newValues = { palletLpnNumbers: mutablePalletLpnNumbers };
      formik.setValues(newValues);
      setInitialValues(newValues);
      setIsDataReady(true);
    }
  }, [ftlData, activePallet, activePallet?.numberOfPallets, ftlDataError, variant, isFtlDataLoading, isOffline]);
  useEffect(() => {
    if (activePallet && isOffline) {
      let initialPalletLpnNumbers: PalletLpnNumber[] = [];

      if (offlineFtlData?.palletLpnNumbers && offlineFtlData?.palletLpnNumbers?.length > 0) {
        initialPalletLpnNumbers = offlineFtlData.palletLpnNumbers.map((pln: PalletLpnNumber) => ({
          ...pln,
          endStatusPallet: pln.endStatusPallet || '',

          ftlRows: pln.ftlRows.length > 0 ? pln.ftlRows.map(row => ({ ...row })) : [{
            variant,
            topMeasured: '',
            middleMeasured: '',
            bottomMeasured: '',
            notes: '',
            ftlState: 'yes',
          }],
        }));
      }

      let mutablePalletLpnNumbers = [...initialPalletLpnNumbers];

      while (mutablePalletLpnNumbers.length < Number(activePallet.numberOfPallets)) {
        mutablePalletLpnNumbers.push({
          lpnNo: '',
          endStatusPallet: '',
          ftlRows: [{
            variant,
            topMeasured: '',
            middleMeasured: '',
            bottomMeasured: '',
            ftlState: 'yes',
            notes: '',
          }],
        });
      }

      const newValues = { palletLpnNumbers: mutablePalletLpnNumbers };
      formik.setValues(newValues);
      setInitialValues(newValues);
      setIsDataReady(true);
    }
  }, [isOffline, activePallet, offlineFtlData]);

  useEffect(() => {
    if (onFormChange && initialValues) {
      const hasChanges = JSON.stringify(formik.values) !== JSON.stringify(initialValues);
      onFormChange(hasChanges);
    }
  }, [formik.values, onFormChange, initialValues]);
  useEffect(() => {
    setIsLoading(formik.isSubmitting);
  }, [formik.isSubmitting, setIsLoading])

  // Helper function to determine if a measurement is within range
  const isWithinRange = (measurement: number, palletName: string, position: 'top' | 'middle' | 'bottom') => {
    if (!consolidatedData || !consolidatedData.palletDetails) return { isWithin: true, isAbove: false, isBelow: false, isNA: false };

    // Find the pallet details in the statistics
    const palletDetail = consolidatedData.palletDetails.find(
      (p: any) => p.palletName === palletName
    );

    if (!palletDetail) return { isWithin: true, isAbove: false, isBelow: false, isNA: false }; // Default if not found

    // For N/A values, return special case
    if (isNaN(measurement) || measurement === 0) {
      return { isWithin: false, isAbove: false, isBelow: false, isNA: true };
    }

    let calculatedTarget = 0;
    let maxTarget = 0;

    // Set target ranges based on position
    if (position === 'top') {
      calculatedTarget = Number(palletDetail.topCalculatedTarget);
      maxTarget = Number(palletDetail.topMax);
    } else if (position === 'middle') {
      calculatedTarget = Number(palletDetail.middleCalculatedTarget);
      maxTarget = Number(palletDetail.middleMax);
    } else if (position === 'bottom') {
      calculatedTarget = Number(palletDetail.bottomCalculatedTarget);
      maxTarget = Number(palletDetail.bottomMax);
    }

    const isAbove = measurement > maxTarget;
    const isBelow = measurement < calculatedTarget;
    const isWithin = !isAbove && !isBelow;

    return { isWithin, isAbove, isBelow, isNA: false };
  };

  // Add roundToNearestWholeNumber function
  const roundToNearestWholeNumber = (value: string): string => {
    if (!value || isNaN(Number(value))) return value;
    const num = parseFloat(value);
    const rounded = Math.round(num);
    return rounded.toString();
  };

  // Add keyboard navigation handler to move focus with arrow keys
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLElement>, rowIndex: number, colIndex: number) => {
    let targetRow = rowIndex;
    let targetCol = colIndex;
    switch (e.key) {
      case 'ArrowUp':
        targetRow = Math.max(0, rowIndex - 1);
        break;
      case 'Enter': // Adding Enter key to behave like Down arrow - Excel behavior
      case 'ArrowDown':
        targetRow = Math.min(formik.values.palletLpnNumbers.length - 1, rowIndex + 1);
        break;
      case 'ArrowLeft':
        targetCol = Math.max(1, colIndex - 1);
        break;
      case 'ArrowRight':
        targetCol = Math.min(6, colIndex + 1);
        break;
      default:
        return;
    }
    e.preventDefault();
    const nextEl = document.querySelector(`[data-row="${targetRow}"][data-col="${targetCol}"]`)
    nextEl?.focus();
  }, [formik.values.palletLpnNumbers.length]);

  // Add a new useEffect to load the consolidated data when component mounts
  useEffect(() => {
    if (projectId && variant) {
      refetchConsolidatedData();
    }
  }, [projectId, variant, refetchConsolidatedData]);

  console.log(offlineFtlData, 'offlineFtlData', formik.values, 'formik.values')
  const endStatusOptions = [{
    label: "Pre-Ship",
    value: "Pre Ship"
  }, {
    label: "Post-Ship",
    value: "Post Ship"
  },
  { label: "Stack start(stacked+30m)", value: "Stack start(stacked+30m)" },
  { label: "Stack Week-1", value: "Stack Week-1" },
  { label: "Stack Week-2", value: "Stack Week-2" },
  { label: "Stack Week-3", value: "Stack Week-3" },
  { label: "Stack Week-4 (unstacking+30M)", value: "Stack Week-4 (unstacking+30M)" }
  ]
  console.log(ftlData, 'ftlData', formik.values, 'formik.values')
  useEffect(() => {
    return () => {
      if (isEditing) {
        setIsEditing(false)
      }
    }
  }, [isEditing])
  return (
    <div className="space-y-4">
      {!isOffline && <EditBanner

        stage={stage}
        palletId={palletId}
        component={tabName}
        onEditStateChange={setIsEditing}
      />}
      <form
        id={`ftl-table-${activePallet?.id}`}

        onSubmit={formik.handleSubmit}
      >
        <Table className={`${(isEditing) || (isOffline) ? " " : "pointer-events-none"} border-collapse`}>
          <TableHeader>
            <TableRow>
              <TableHead className="border border-gray-300 text-blue p-0 px-3 text-[10px] md:text-sm font-semibold md:font-bold bg-light-blue text-left" colSpan={4}>
                Pallet Name: {activePallet?.name || 'N/A'} - Mode {activePallet?.shippingModes?.map((sp) => sp.mode).join(', ') || 'N/A'}
              </TableHead>
              <TableHead className="border border-gray-300 text-black p-0 px-3 text-[10px] md:text-sm font-semibold md:font-bold bg-light-blue text-center" colSpan={4}>
             { !isOffline &&   <div className='flex items-center gap-2 justify-between !pointer-events-auto'>

                  <div className="flex-center gap-4 justify-end ml-auto">
                    <Button
                      onClick={() => setIsAdvancedControlsOpen(true)}
                      type='button'
                      className="bg-white text-md hover:bg-blue hover:text-white font-semibold text-blue border-2 border-blue"
                    >
                      Advance Controls
                    </Button>
                    {/* <Button type='button' className="bg-white text-md font-semibold text-blue border-2 border-blue">
                      Clear All
                    </Button> */}
                    <Button
                      onClick={() => setIsConsolidatedViewOpen(true)}
                      type='button' className="bg-white hover:bg-blue hover:text-white text-md font-semibold text-blue border-2 border-blue">
                      Consolidated View
                    </Button>
                  </div>
                </div>}

                {/* Advanced Controls Modal */}
                {isAdvancedControlsOpen && (
                  <div className="fixed inset-0 !pointer-events-auto  bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white p-6 rounded-lg w-[500px] text-base">
                      <h2 className="text-xl font-bold mb-4">Advanced Controls</h2>
                      <div className="space-y-4">
                        <div className="flex flex-col gap-2">
                          <label htmlFor="forceToLoadData" className="text-base">Force To Load Data - {variant === 'preShip' ? 'Pre Ship' : variant === 'postShip' ? 'Post Ship' : ''}</label>
                          <div className="flex items-center gap-4 justify-center">
                            <div>
                              <label htmlFor="percentage" className="block text-sm">Percentage</label>
                              <input
                                id="percentage"
                                name='percentage'
                                onChange={(e) => paramsFormik.setFieldValue('percentage', e.target.value)}
                                value={paramsFormik.values.percentage}
                                type='number'
                                className="border-2 border-blue px-2 py-1 w-[100px] text-base"
                              />
                            </div>
                            <div>
                              <label htmlFor="plusByUser" className="block text-sm">Plus by User</label>
                              <input
                                id="plusByUser"
                                name='plusByUser'
                                onChange={(e) => paramsFormik.setFieldValue('plusByUser', e.target.value)}
                                value={paramsFormik.values.plusByUser}
                                type='number'
                                className="border-2 border-blue px-2 py-1 w-[100px] text-base"
                              />
                            </div>
                          </div>
                        </div>
                        <div className="flex justify-end gap-2">
                          <Button
                            type='button'
                            onClick={() => setIsAdvancedControlsOpen(false)}
                            className="bg-gray-200 text-gray-800 hover:bg-bgRed hover:text-white"
                          >
                            Cancel
                          </Button>
                          <Button
                            type='button'
                            onClick={() => {
                              paramsFormik.submitForm();
                              setIsAdvancedControlsOpen(false);
                            }}
                            disabled={updateProjectLoading}
                            className="flex items-center gap-2 bg-blue"
                          >
                            {updateProjectLoading && <Loader2 className='animate-spin' />}
                            Update
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </TableHead>
            </TableRow>
            <TableRow>
              <TableHead className={cn(
                "border border-gray-300 w-[150px] text-black p-0 text-left px-3 text-[10px] relative md:text-sm font-semibold md:font-bold bg-light-blue",
                !showPalletNo && "w-[20px] bg-blue"
              )}>
                {showPalletNo && "Pallet No"}
                <button
                  onClick={() => setShowPalletNo(!showPalletNo)}
                  className={cn(
                    "cursor-pointer !pointer-events-auto  hover:scale-125 text-white transition-all",
                    showPalletNo && "right-2 top-4 absolute"
                  )}
                >
                  {showPalletNo ? (
                    <ChevronsLeft size={20} className="text-blue" />
                  ) : (
                    <ChevronsRight size={20} className="text-white" />
                  )}
                </button>
              </TableHead>
              <TableHead className={cn(
                "border border-gray-300 w-[150px] text-black p-0 text-left px-3 text-[10px] relative md:text-sm font-semibold md:font-bold bg-light-blue",
                !showLpnNo && "w-[20px] bg-blue"
              )}>
                {showLpnNo && "Pallet Identifier"}
                <button
                  onClick={() => setShowLpnNo(!showLpnNo)}
                  className={cn(
                    "cursor-pointer !pointer-events-auto  hover:scale-125 text-white transition-all",
                    showLpnNo && "right-2 top-4 absolute"
                  )}
                >
                  {showLpnNo ? (
                    <ChevronsLeft size={20} className="text-blue" />
                  ) : (
                    <ChevronsRight size={20} className="text-white" />
                  )}
                </button>
              </TableHead>
              <TableHead className={cn(
                "border border-gray-300 w-[150px] text-black p-0 text-left px-3 text-[10px] relative md:text-sm font-semibold md:font-bold bg-light-blue",
                !showEndStatus && "w-[20px] bg-blue"
              )}>
                {showEndStatus && "End Status"}
                <button
                  onClick={() => setShowEndStatus(!showEndStatus)}
                  className={cn(
                    "cursor-pointer !pointer-events-auto  hover:scale-125 text-white transition-all",
                    showEndStatus && "right-2 top-4 absolute"
                  )}
                >
                  {showEndStatus ? (
                    <ChevronsLeft size={20} className="text-blue" />
                  ) : (
                    <ChevronsRight size={20} className="text-white" />
                  )}
                </button>
              </TableHead>
              <TableHead className="border border-gray-300 w-[150px] text-black p-0 text-left px-3 text-[10px] relative md:text-sm font-semibold md:font-bold bg-light-blue">
                FTL State (yes/no)
              </TableHead>
              {["Top (lbs)", "Middle (lbs)", "Bottom (lbs)", "Notes"].map((head) => (
                <TableHead
                  key={head}
                  className="border border-gray-300 text-black p-0 px-3 text-[10px] md:text-sm font-semibold md:font-bold bg-light-blue text-left"
                >
                  {head}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {formik.values.palletLpnNumbers.map((pln, index) => {
              const isRowEditable = week.includes(pln.endStatusPallet) && pln?.ftlRows[0]?.ftlState === 'yes';

              // Determine if values are within range
              const topMeasured = pln.ftlRows[0]?.topMeasured || '';
              const middleMeasured = pln.ftlRows[0]?.middleMeasured || '';
              const bottomMeasured = pln.ftlRows[0]?.bottomMeasured || '';

              const topMeasurement = topMeasured === '' ? NaN : parseFloat(topMeasured);
              const middleMeasurement = middleMeasured === '' ? NaN : parseFloat(middleMeasured);
              const bottomMeasurement = bottomMeasured === '' ? NaN : parseFloat(bottomMeasured);

              const topStatus = activePallet ? isWithinRange(topMeasurement, activePallet.name, 'top') : { isWithin: true, isAbove: false, isBelow: false, isNA: true };
              const middleStatus = activePallet ? isWithinRange(middleMeasurement, activePallet.name, 'middle') : { isWithin: true, isAbove: false, isBelow: false, isNA: true };
              const bottomStatus = activePallet ? isWithinRange(bottomMeasurement, activePallet.name, 'bottom') : { isWithin: true, isAbove: false, isBelow: false, isNA: true };

              return (
                <TableRow key={index}>
                  <TableCell className="border border-gray-300 p-0 bg-states-disabled">
                    {showPalletNo && (
                      <Input
                        className="w-full  border-none "
                        value={index + 1}
                        readOnly
                      />
                    )}
                  </TableCell>
                  <TableCell className="border border-gray-300 p-0 bg-states-disabled">
                    {showLpnNo && (
                      <Input
                        className="w-full border-none "
                        value={pln.lpnNo}
                        readOnly
                      />
                    )}
                  </TableCell>
                  <TableCell className={cn("border border-gray-300 p-0", !showEndStatus && "bg-blue")}>
                    {showEndStatus && (
                      <Select
                        value={pln.endStatusPallet || undefined}
                        onValueChange={(value) => formik.setFieldValue(`palletLpnNumbers.${index}.endStatusPallet`, value)}
                      >
                        <SelectTrigger
                          data-row={index} data-col={1}
                          onKeyDown={(e) => handleKeyDown(e, index, 1)}
                          className="w-full rounded-none bg-transparent border-2 border-required focus:border-blue"
                        >
                          <SelectValue placeholder="Select End Status" />
                        </SelectTrigger>
                        <SelectContent>
                          {endStatusOptions.map((status) => (
                            <SelectItem key={status.value} value={status.value}>
                              {status.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </TableCell>
                  <TableCell className="border border-gray-300 p-0">
                    <Select
                      value={formik.values.palletLpnNumbers[index].ftlRows[0].ftlState}
                      onValueChange={(value) => formik.setFieldValue(`palletLpnNumbers.${index}.ftlRows[0].ftlState`, value)}
                    >
                      <SelectTrigger
                        data-row={index} data-col={2}
                        onKeyDown={(e) => handleKeyDown(e, index, 2)}
                        className="w-full rounded-none bg-transparent border-2 border-required focus:border-blue"
                      >
                        <SelectValue placeholder="Select FTL State" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="yes">Yes</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>

                  {/* Field inputs with color coding */}
                  <TableCell key="topMeasured" className={cn(
                    "border border-gray-300 p-0 relative",
                    isRowEditable && topMeasured && !topStatus.isNA && (
                      topStatus.isWithin ? "bg-states-success" : "bg-bgRed"
                    )
                  )}>
                    <Input
                      data-row={index} data-col={3}
                      onKeyDown={(e) => handleKeyDown(e, index, 3)}
                      className={cn(
                        "w-full h-9 border-none pr-6",
                        !isRowEditable && "bg-gray-200",
                        isRowEditable && topMeasured && !topStatus.isNA && (
                          topStatus.isWithin ? "text-white" : "text-white"
                        )
                      )}
                      type="number"
                      step="1"
                      placeholder="Enter"
                      value={formik.values.palletLpnNumbers[index].ftlRows[0].topMeasured}
                      onChange={(e) => {
                        formik.setFieldValue(`palletLpnNumbers.${index}.ftlRows[0].topMeasured`, e.target.value);
                      }}
                      onBlur={(e) => {
                        if (e.target.value) {
                          const roundedValue = roundToNearestWholeNumber(e.target.value);
                          formik.setFieldValue(`palletLpnNumbers.${index}.ftlRows[0].topMeasured`, roundedValue);
                        }
                      }}
                      readOnly={!isRowEditable}
                      disabled={!isRowEditable}
                    />
                    {isRowEditable && topMeasured && !topStatus.isNA && !topStatus.isWithin && (
                      <span className="absolute right-2 top-1/2 transform -translate-y-1/2 text-white font-bold">
                        {topStatus.isAbove ? "↑" : "↓"}
                      </span>
                    )}
                  </TableCell>

                  <TableCell key="middleMeasured" className={cn(
                    "border border-gray-300 p-0 relative",
                    isRowEditable && middleMeasured && !middleStatus.isNA && (
                      middleStatus.isWithin ? "bg-states-success" : "bg-bgRed"
                    )
                  )}>
                    <Input
                      data-row={index} data-col={4}
                      onKeyDown={(e) => handleKeyDown(e, index, 4)}
                      className={cn(
                        "w-full h-9 border-none pr-6",
                        !isRowEditable && "bg-gray-200",
                        isRowEditable && middleMeasured && !middleStatus.isNA && (
                          middleStatus.isWithin ? "text-white" : "text-white"
                        )
                      )}
                      type="number"
                      step="1"
                      placeholder="Enter"
                      value={formik.values.palletLpnNumbers[index].ftlRows[0].middleMeasured}
                      onChange={(e) => {
                        formik.setFieldValue(`palletLpnNumbers.${index}.ftlRows[0].middleMeasured`, e.target.value);
                      }}
                      onBlur={(e) => {
                        if (e.target.value) {
                          const roundedValue = roundToNearestWholeNumber(e.target.value);
                          formik.setFieldValue(`palletLpnNumbers.${index}.ftlRows[0].middleMeasured`, roundedValue);
                        }
                      }}
                      readOnly={!isRowEditable}
                      disabled={!isRowEditable}
                    />
                    {isRowEditable && middleMeasured && !middleStatus.isNA && !middleStatus.isWithin && (
                      <span className="absolute right-2 top-1/2 transform -translate-y-1/2 text-white font-bold">
                        {middleStatus.isAbove ? "↑" : "↓"}
                      </span>
                    )}
                  </TableCell>

                  <TableCell key="bottomMeasured" className={cn(
                    "border border-gray-300 p-0 relative",
                    isRowEditable && bottomMeasured && !bottomStatus.isNA && (
                      bottomStatus.isWithin ? "bg-states-success" : "bg-bgRed"
                    )
                  )}>
                    <Input
                      data-row={index} data-col={5}
                      onKeyDown={(e) => handleKeyDown(e, index, 5)}
                      className={cn(
                        "w-full h-9 border-none pr-6",
                        !isRowEditable && "bg-gray-200",
                        isRowEditable && bottomMeasured && !bottomStatus.isNA && (
                          bottomStatus.isWithin ? "text-white" : "text-white"
                        )
                      )}
                      type="number"
                      step="1"
                      placeholder="Enter"
                      value={formik.values.palletLpnNumbers[index].ftlRows[0].bottomMeasured}
                      onChange={(e) => {
                        formik.setFieldValue(`palletLpnNumbers.${index}.ftlRows[0].bottomMeasured`, e.target.value);
                      }}
                      onBlur={(e) => {
                        if (e.target.value) {
                          const roundedValue = roundToNearestWholeNumber(e.target.value);
                          formik.setFieldValue(`palletLpnNumbers.${index}.ftlRows[0].bottomMeasured`, roundedValue);
                        }
                      }}
                      readOnly={!isRowEditable}
                      disabled={!isRowEditable}
                    />
                    {isRowEditable && bottomMeasured && !bottomStatus.isNA && !bottomStatus.isWithin && (
                      <span className="absolute right-2 top-1/2 transform -translate-y-1/2 text-white font-bold">
                        {bottomStatus.isAbove ? "↑" : "↓"}
                      </span>
                    )}
                  </TableCell>

                  <TableCell className="border border-gray-300 p-0">
                    <Input
                      data-row={index} data-col={6}
                      onKeyDown={(e) => handleKeyDown(e, index, 6)}
                      className={cn("w-full h-9 border-none", !isRowEditable && "bg-gray-200")}
                      placeholder="Enter notes"
                      {...formik.getFieldProps(`palletLpnNumbers.${index}.ftlRows.0.notes`)}
                      readOnly={!isRowEditable}
                      disabled={!isRowEditable}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <button type="submit" style={{ display: 'none' }}></button>
        {<FTLConsolidatedView
          isOpen={isConsolidatedViewOpen}
          onClose={() => setIsConsolidatedViewOpen(false)}
          variant={variant}
        />}
      </form>
    </div>
  );
};

export default Ftl;
